import React, { useState } from 'react';
import { Copy, Check, User, Calendar, CreditCard, Phone, MapPin, Shield } from 'lucide-react';

function CopyField({ label, value, icon: Icon, mono = false }) {
  const [copied, setCopied] = useState(false);

  if (!value) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="flex items-center justify-between py-1.5 group">
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={14} className="text-slate-400 flex-shrink-0" />
        <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
      </div>
      <div className="flex items-center gap-1.5 ml-2 min-w-0 overflow-hidden">
        <span className={`text-sm font-medium text-slate-800 truncate ${mono ? 'font-mono' : ''}`}>
          {value}
        </span>
        <button
          onClick={handleCopy}
          className={`p-1 rounded transition-all flex-shrink-0 ${
            copied
              ? 'text-emerald-500 bg-emerald-50'
              : 'text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 opacity-0 group-hover:opacity-100'
          }`}
          title="Copy"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
    </div>
  );
}

export default function ClientQuickReference({ client }) {
  if (!client) return null;

  const formatDate = (d) => {
    if (!d) return null;
    try {
      return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();
  const address = [client.address, client.city, client.state, client.zip].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-purple-50">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <User size={15} className="text-indigo-500" />
          Client Quick Reference
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">Click any value to copy</p>
      </div>
      <div className="px-4 py-2 divide-y divide-slate-50">
        <CopyField label="Name" value={fullName} icon={User} />
        <CopyField label="DOB" value={formatDate(client.dob)} icon={Calendar} mono />
        <CopyField label="Phone" value={client.phone} icon={Phone} mono />
        {client.medicareId && (
          <CopyField label="MBI" value={client.medicareId} icon={CreditCard} mono />
        )}
        <CopyField label="Carrier" value={client.carrier} icon={Shield} />
        <CopyField label="Plan" value={client.plan} icon={CreditCard} />
        <CopyField label="Type" value={client.planType} icon={CreditCard} />
        <CopyField label="Effective" value={formatDate(client.effectiveDate)} icon={Calendar} mono />
        {address && <CopyField label="Address" value={address} icon={MapPin} />}
      </div>
    </div>
  );
}
